import { useMemo } from 'react';

const useCalculateSlidePagerTranslation = (
  maxVisibleDots: number,
  numberOfElements: number,
  activeIndex: number,
  dotSize: number,
  dotMargin: number,
  dotExpandedSize: number,
) =>
  useMemo(() => {
    // We don't need to move the pager if there are no limitation on visible dots
    // if there are less elements than the max visible dots
    // or if active index is less than the maxVisibleDots / 2
    if (
      !maxVisibleDots ||
      numberOfElements <= maxVisibleDots ||
      activeIndex < maxVisibleDots / 2
    )
      return 'translateX(0)';
    // if there are more elements than the maximum visible dots,
    // we need to know the space of each dot and its margin
    // to move the pager to the correct position
    const margins = 2 * dotMargin;
    const spacePerDot = dotSize + margins;
    const spacePerDotExpanded = dotExpandedSize + margins;

    // active element will be always visible, so the min with will be
    // spacePerDotExpanded + spacePerDot * (maxVisibleDots - 1)
    // ie with 4 maxVisibleDots:  [(__) () () ()]
    const slicePagerWidth =
      spacePerDotExpanded + spacePerDot * (maxVisibleDots - 1);

    // max width will be the space that needs all the elements taking incount the expanded size
    const totalPagerWidth =
      spacePerDotExpanded + spacePerDot * (numberOfElements - 1);

    // if we are on the last slice of the pager, we need to show a full slice of
    // the pager, and stop translation there () () () () [() (__) () ()]
    if (activeIndex >= numberOfElements - maxVisibleDots / 2)
      return `translateX(-${totalPagerWidth - slicePagerWidth}px)`;

    // after maxVisibleDots / 2 we need to move the pager to the left
    // spacePerDot distance so the active element is always visible in the middle
    // of the pager () () () [() (__) () ()] () () ()
    if (activeIndex >= maxVisibleDots / 2)
      return `translateX(-${
        spacePerDot * (activeIndex - maxVisibleDots / 2) + dotSize
      }px)`;

    return 'translateX(0)';
  }, [
    maxVisibleDots,
    numberOfElements,
    activeIndex,
    dotSize,
    dotMargin,
    dotExpandedSize,
  ]);

export { useCalculateSlidePagerTranslation };
